import { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RosterBoard } from "@/components/RosterBoard";
import { shiftColours, CELL_NAMES, downloadBlob } from "@/components/roster/shiftColours";
import { formatTime12, shiftInfo } from "@/lib/format";
import { CheckCircle2, Send, Undo2, FileSpreadsheet, FileText, RefreshCw, Clock, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-800 border-slate-300", icon: Clock },
  submitted: { label: "Waiting for approval", cls: "bg-amber-50 text-amber-900 border-amber-300", icon: Send },
  approved: { label: "Approved & published", cls: "bg-emerald-50 text-emerald-900 border-emerald-300", icon: CheckCircle2 },
  rejected: { label: "Sent back for changes", cls: "bg-rose-50 text-rose-900 border-rose-300", icon: AlertTriangle },
};

function StatusBanner({ plan }) {
  const s = STATUS[plan.status || "draft"];
  const Icon = s.icon;
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border-2 px-5 py-4 ${s.cls}`} data-testid="roster-status-banner">
      <Icon className="h-6 w-6" />
      <div>
        <p className="text-base font-bold" data-testid="roster-status">{s.label}</p>
        <p className="text-sm">
          {plan.submitted_by && `Submitted by ${plan.submitted_by}. `}
          {plan.approved_by && plan.status === "approved" && `Approved by ${plan.approved_by}. `}
          {plan.review_comment && <span className="font-semibold">Comment: {plan.review_comment}</span>}
        </p>
      </div>
    </div>
  );
}

function StaffingChart({ board, shifts, colours }) {
  const codes = shifts.map((s) => s.code);
  const data = board.dates.map((d) => {
    const row = { day: String(+d.slice(8)) };
    codes.forEach((c) => { row[c] = 0; });
    Object.values(board.roster).forEach((cells) => { if (codes.includes(cells[d])) row[cells[d]] += 1; });
    return row;
  });
  return (
    <Card className="p-5">
      <p className="mb-1 text-base font-bold text-slate-900">People on duty each day</p>
      <p className="mb-3 text-sm text-slate-600">Each bar is one day, split by shift.</p>
      <div className="h-64" data-testid="staffing-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="day" tick={{ fontSize: 13, fill: "#334155" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 13, fill: "#334155" }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 14 }} />
            {codes.map((c) => <Bar key={c} dataKey={c} stackId="a" fill={colours[c]} stroke="#475569" strokeWidth={0.3} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function SummaryTable({ board, shifts, colours }) {
  const codes = shifts.map((s) => s.code);
  return (
    <Card className="p-5">
      <p className="mb-3 text-base font-bold text-slate-900">Per-employee summary</p>
      <div className="max-h-[60vh] overflow-auto">
      <table className="w-full text-sm" data-testid="roster-summary">
        <thead className="sticky top-0 z-10 bg-white"><tr className="border-b-2 border-slate-200 text-left text-slate-700">
          <th className="py-2 pr-3 font-semibold">Employee</th><th className="px-2 font-semibold">Working days</th>
          {codes.map((c) => <th key={c} className="px-2 font-semibold"><span className="rounded px-2 py-0.5" style={{ background: colours[c] }}>{c}</span></th>)}
          <th className="px-2 font-semibold">Week offs</th><th className="px-2 font-semibold">Leave</th><th className="px-2 font-semibold">Holidays</th>
        </tr></thead>
        <tbody>
          {board.employees.map((e) => {
            const cells = Object.values(board.roster[e.key] || {});
            const n = (x) => cells.filter((c) => c === x).length;
            return (
              <tr key={e.id} className="border-b border-slate-100 text-slate-800" data-testid={`summary-${e.id}`}>
                <td className="py-2 pr-3 font-semibold">{e.name}</td>
                <td className="px-2 font-bold">{cells.filter((c) => codes.includes(c)).length}</td>
                {codes.map((c) => <td key={c} className="px-2">{n(c)}</td>)}
                <td className="px-2">{n("WO")}</td><td className="px-2">{n("L")}</td><td className="px-2">{n("H")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </Card>
  );
}

function ReviewActions({ board, onChanged, onRegenerate }) {
  const { isManagerOnly } = useAuth();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState("");
  const plan = board.plan;
  const status = plan.status || "draft";
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const fname = `roster_${plan.year}_${String(plan.month).padStart(2, "0")}`;
  const viewOnly = board.can_edit === false;
  const manual = plan.method === "manual";
  const empty = manual ? board.employees.reduce((n, e) => n + board.dates.filter((d) => !board.roster[e.key]?.[d]).length, 0) : 0;
  const okEmpty = () => !empty || window.confirm(`${empty} day(s) in this roster are still empty. Continue anyway?`);
  const redo = () => { if (!manual || window.confirm("Going back to setup and reopening the grid clears the shifts you painted. Continue?")) onRegenerate(); };
  return (
    <div className="flex flex-wrap gap-2">
      {viewOnly && <span className="self-center rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700" data-testid="view-only-badge">View only</span>}
      {status !== "submitted" && !viewOnly && <Button variant="outline" onClick={redo} data-testid="regenerate-btn"><RefreshCw className="mr-2 h-4 w-4" /> {manual ? "Change setup" : "Regenerate"}</Button>}
      {isManagerOnly && (status === "approved" || plan.locked_for_cm) && <span className="self-center rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-800" data-testid="approved-locked-note">Approved · only your org admin can change it</span>}
      {isManagerOnly && !viewOnly && status === "submitted" && <ChangeRequestButton plan={plan} onChanged={onChanged} />}
      {!isManagerOnly && status === "submitted" && plan.change_request?.status === "pending" && <ChangeRequestReview plan={plan} onChanged={onChanged} />}
      {isManagerOnly && !viewOnly && ["draft", "rejected"].includes(status) && (
        <Button disabled={!board.has_draft} onClick={() => okEmpty() && run(() => api.post(`/roster/plans/${plan.id}/submit`), "Sent to your org admin for approval")} data-testid="submit-approval-btn">
          <Send className="mr-2 h-4 w-4" /> Submit for approval
        </Button>
      )}
      {!isManagerOnly && status !== "approved" && (
        <Button disabled={!board.has_draft} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => okEmpty() && run(() => api.post(`/roster/plans/${plan.id}/approve`), "Roster approved and published")} data-testid="approve-btn">
          <CheckCircle2 className="mr-2 h-4 w-4" /> Approve & publish
        </Button>
      )}
      {!isManagerOnly && status === "submitted" && (
        <Button variant="outline" className="border-rose-300 text-rose-700" onClick={() => setRejectOpen(true)} data-testid="reject-btn"><Undo2 className="mr-2 h-4 w-4" /> Send back</Button>
      )}
      <Button variant="outline" disabled={!board.has_draft} onClick={() => downloadBlob(api, `/roster/plans/${plan.id}/export.xlsx`, `${fname}.xlsx`)} data-testid="export-xlsx-btn"><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel</Button>
      <Button variant="outline" disabled={!board.has_draft} onClick={() => downloadBlob(api, `/roster/plans/${plan.id}/export.pdf`, `${fname}.pdf`)} data-testid="export-pdf-btn"><FileText className="mr-2 h-4 w-4" /> PDF</Button>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send back for changes</DialogTitle><DialogDescription>Tell the manager what needs to change.</DialogDescription></DialogHeader>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Need 2 people on night shift on weekends" data-testid="reject-comment" />
          <DialogFooter>
            <Button onClick={() => { setRejectOpen(false); run(() => api.post(`/roster/plans/${plan.id}/reject`, { comment }), "Sent back to the manager"); }} data-testid="reject-submit">Send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangeRequestButton({ plan, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (plan.change_request?.status === "pending") {
    return <span className="self-center rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-900" data-testid="change-request-pending">Change request sent · waiting for org admin</span>;
  }
  const send = async () => {
    setBusy(true);
    try { await api.post(`/roster/plans/${plan.id}/change-request`, { reason }); toast.success("Change request sent to your org admin"); setOpen(false); setReason(""); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="request-change-btn"><Pencil className="mr-2 h-4 w-4" /> Request changes</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request changes</DialogTitle><DialogDescription>Your org admin gets this request. If they accept, the roster opens again as a draft for you to edit and resubmit.</DialogDescription></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Need to swap Rahul's week off in week 2" data-testid="change-request-reason" />
          <DialogFooter><Button disabled={!reason.trim() || busy} onClick={send} data-testid="change-request-submit">Send request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ChangeRequestReview({ plan, onChanged }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const cr = plan.change_request;
  const decide = async (accept) => {
    setBusy(true);
    try { await api.post(`/roster/plans/${plan.id}/change-request/decide`, { accept, comment }); toast.success(accept ? "Roster reopened for the manager" : "Request declined"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="w-full rounded-lg border-2 border-amber-300 bg-amber-50 p-4" data-testid="change-request-review">
      <p className="text-base font-bold text-amber-900">{cr.requested_by} asked to change this roster</p>
      <p className="mt-1 text-sm text-amber-900" data-testid="change-request-text">“{cr.reason}”</p>
      <Textarea className="mt-3 bg-white" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment for the manager" data-testid="change-request-comment" />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => decide(true)} data-testid="change-request-accept">Accept and reopen</Button>
        <Button disabled={busy} variant="outline" onClick={() => decide(false)} data-testid="change-request-decline">Decline</Button>
      </div>
    </div>
  );
}

function ShiftLegend({ shifts, colours }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="shift-legend">
      {shifts.map((s) => (
        <span key={s.code} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800">
          <span className="rounded px-2 py-0.5 font-bold" style={{ background: colours[s.code] }}>{s.code}</span>
          {s.name} · {formatTime12(s.start)} – {formatTime12(s.end)}{shiftInfo(s.start, s.end)?.overnight ? " (next day)" : ""}
        </span>
      ))}
      {Object.entries(CELL_NAMES).map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800">
          <span className="rounded px-2 py-0.5 font-bold" style={{ background: colours[k] }}>{k}</span>{v}
        </span>
      ))}
    </div>
  );
}

export function RosterReview({ board, shifts, masters, onChanged, onRegenerate }) {
  const { isManagerOnly } = useAuth();
  const colours = useMemo(() => shiftColours(shifts), [shifts]);
  const planShifts = shifts.filter((s) => !board.plan.shift_codes?.length || board.plan.shift_codes.includes(s.code));
  const submitted = board.plan.status === "submitted";
  const locked = (submitted && isManagerOnly) || board.can_edit === false;
  const edits = board.plan.admin_edits || [];
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusBanner plan={board.plan} />
        <ReviewActions board={board} onChanged={onChanged} onRegenerate={onRegenerate} />
      </div>
      <ShiftLegend shifts={planShifts} colours={colours} />
      {submitted && !isManagerOnly && <p className="rounded-lg bg-indigo-50 px-4 py-2.5 text-sm text-indigo-900" data-testid="admin-edit-note">You can change shifts on this submitted roster before approving. The center manager who submitted it is notified of each change.</p>}
      {submitted && !!edits.length && (
        <details className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="admin-edits-list">
          <summary className="cursor-pointer font-semibold">{edits.length} change(s) made by admin after submission</summary>
          <ul className="mt-2 list-disc space-y-0.5 pl-5">{[...edits].reverse().slice(0, 30).map((e, i) => <li key={i}>{e.what} <span className="text-amber-700">— {e.by}</span></li>)}</ul>
        </details>
      )}
      {board.has_draft && <StaffingChart board={board} shifts={planShifts} colours={colours} />}
      <RosterBoard board={board} shifts={planShifts} masters={masters} onChanged={onChanged} colours={colours} locked={locked} paint={board.plan.method === "manual" && !locked} />
      {board.has_draft && <SummaryTable board={board} shifts={planShifts} colours={colours} />}
    </div>
  );
}
